import { FC, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useFormContext } from 'react-hook-form';
import Link from 'next/link';
import Pagination from '@components/common/pagination';
import Icon from '@components/common/assets';
import { PageState } from '@domain/rs/common/PageableRs';
import Button from '@components/common/button';
import { useLoginModal } from '@components/loginModal/LoginModalContext';
import { usePassReviewPosts } from '@repository/passReview/usePassReview';
import PassReviewPostListGetRq from '@domain/rq/passReview/PassReviewPostListGetRq';
import { PassReviewListVO } from '@domain/vo/passReview/PassReviewPostListGetVO';
import { RouteUrl } from '@utils/routeUtil';
import classnames from 'classnames/bind';
import styles from './index.module.scss';
const cx = classnames.bind(styles);

interface ItemProps {
  data: PassReviewListVO;
}

const Item: FC<ItemProps> = ({ data }) => {
  return (
    <Link className={cx('review')} href={`${RouteUrl.PASSBOARD}/${data.sn}`}>
      <div className={cx('type', 'column')}>
        <span className={cx('text', { notice: data.type === 'NOTICE' }, { best: data.type === 'BEST' })}>
          {data.typeText}
        </span>
      </div>
      <div className={cx('title', 'column')}>
        <span className={cx('text')}>{data.title}</span>
        <span className={cx('comment')}>{`(${data.commentCount > 99 ? '99+' : data.commentCount})`}</span>
      </div>
      <div className={cx('writer', 'column')}>{data.displayId}</div>
      <div className={cx('createDate', 'column')}>{data.createdDateTime}</div>
      <div className={cx('view', 'column')}>{data.views}</div>
    </Link>
  );
};

const ReviewList = () => {
  const router = useRouter();
  const { openLoginModal, isLogin } = useLoginModal();
  const { getValues, reset, watch } = useFormContext();
  const { data } = usePassReviewPosts(new PassReviewPostListGetRq(getValues() as PassReviewPostListGetRq));

  const passReviewData = data?.pages[0];

  const reviewEls = passReviewData?.posts.map((item) => <Item key={item.sn} data={item} />);

  const clickWriteBtn = () => {
    if (!isLogin) openLoginModal({ redirectUrl: RouteUrl.PASSBOARD_NEW });
    else router.push(RouteUrl.PASSBOARD_NEW);
  };

  useEffect(() => {
    if (!data) return;
    reset({ ...getValues(), ...passReviewData?.pages });
  }, [data]);

  return (
    <div className={cx('wrapper')}>
      <div className={cx('label')}>
        <div className={cx('type', 'column')}>구분</div>
        <div className={cx('title', 'column')}>제목</div>
        <div className={cx('writer', 'column')}>작성자</div>
        <div className={cx('createDate', 'column')}>작성일</div>
        <div className={cx('view', 'column')}>조회수</div>
      </div>

      <div className={cx('reviewArea')}>{reviewEls}</div>

      <div className={cx('bottom')}>
        <Pagination
          className={cx('pagenation')}
          pagesVO={getValues() as PageState}
          currentPage={watch('page')}
          setCurrentPage={(newPage) => {
            reset({ ...getValues(), page: newPage });
          }}
          isLeftOn={watch('page') > 0 ?? false}
          isRightOn={watch('page') + 1 < (getValues().totalPages ?? 0) ?? false}
        />

        <Button className={cx('writeBtn')} onClick={clickWriteBtn} theme="secondary">
          <Icon name="guideLight" width={24} height={24} />
          후기 작성하기
        </Button>
      </div>
    </div>
  );
};

export default ReviewList;
